import { useState, useRef } from "react";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import MetaTags from "../../componentes/MetaTags";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../styles/circuit-background.css";
import {
  faEnvelope,
  faPhone,
  faMailBulk,
  faHeadset,
  faPaperPlane,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { InlineSpinner } from "../../componentes/LoadingIndicator";
import {
  faSquareFacebook,
  faLinkedin,
  faTelegram,
} from "@fortawesome/free-brands-svg-icons";
import { submitContactForm } from '../../api/contact';
import { Toaster, toast } from 'react-hot-toast';
import { sanitizeFormData } from '../../utils/security';
import ReCaptcha from '../../componentes/ReCaptcha';

const ContactUs = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const contactMethods = [
    {
      icon: faPhone,
      title: t("contact.emergency_contact.title"),
      primary: t("contact.emergency_contact.hotline"),
      secondary: [
        t("contact.emergency_contact.phone1"),
        t("contact.emergency_contact.phone2"),
      ],
    },
    {
      icon: faEnvelope,
      title: t("contact.email.title"),
      primary: t("contact.email.primary"),
      secondary: t("contact.email.support"),
    },
    {
      icon: faMailBulk,
      title: t("contact.location.pobox_title"),
      primary: t("contact.location.pobox"),
      secondary: null,
    },
  ];

  const socialLinks = [
    {
      icon: faSquareFacebook,
      url: "https://facebook.com/ethiocert",
      color: "hover:text-[#1877F2]",
      title: t("contact.social.facebook"),
    },
    {
      icon: faLinkedin,
      url: "https://linkedin.com/company/ethio-cert",
      color: "hover:text-[#0A66C2]",
      title: t("contact.social.linkedin"),
    },
    {
      icon: faTelegram,
      url: "https://t.me/Ethio_Cert_933",
      color: "hover:text-[#0088cc]",
      title: t("contact.social.telegram"),
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!recaptchaToken) {
        toast.error(t("contact.form.recaptcha_required"), {
          duration: 5000,
          style: {
            padding: '16px',
            borderRadius: '8px',
          },
        });
        setLoading(false);
        return;
      }

      const { sanitized, issues } = sanitizeFormData(formData);

      if (issues.length > 0) {
        toast.error(t("contact.form.security_error"), {
          duration: 5000,
          style: {
            padding: '16px',
            borderRadius: '8px',
          },
        });
        setLoading(false);
        return;
      }

      // Add reCAPTCHA token to form data
      const formDataWithRecaptcha = {
        ...sanitized,
        recaptcha: recaptchaToken
      };

      await submitContactForm(formDataWithRecaptcha);
      
      toast.success(t("contact.form.success_message"), {
        duration: 5000,
        style: {
          padding: '16px',
          borderRadius: '8px',
        },
      });
      
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      
      // Reset reCAPTCHA
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      console.error("Error:", error);
      
      if (error.response?.status === 429) {
        toast.error(t("contact.form.rate_limit_error"), {
          duration: 5000,
          style: {
            padding: '16px',
            borderRadius: '8px',
          },
        });
      } else {
        toast.error(t("contact.form.error_message"), {
          duration: 5000,
          style: {
            padding: '16px',
            borderRadius: '8px',
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MetaTags title="Contact Us — Ethio-CERT" description="Contact Ethio-CERT for incident reporting, enquiries and support." url="https://ethiocert.gov.et/contact-us" />
      <PageHeader />
      <GradientBanner
        title={t("contact.title")}
        description={t("contact.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.contact"), link: null },
        ]}
        backgroundIcons={[faHeadset, faEnvelope, faPhone]}
      />
      <Toaster position="top-right" />
      
      <div className="circuit-page-background py-16 font-nunito-sans">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <FontAwesomeIcon
                    icon={method.icon}
                    className="text-primary text-xl"
                  />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-heading font-nunito-sans">
                  {method.title}
                </h3>
                <p className="text-gray-600 text-body font-nunito-sans">
                  {method.primary}
                </p>
                {Array.isArray(method.secondary) ? (
                  <div className="space-y-1">
                    {method.secondary.map((item, i) => (
                      <p
                        key={i}
                        className="text-gray-600 text-body font-nunito-sans"
                      >
                        {item}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-body font-nunito-sans">
                    {method.secondary}
                  </p>
                )}
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <h2 className="text-h-5 font-semibold mb-6 text-heading">
                  {t("contact.form.title")}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-body font-medium text-gray-700 mb-1">
                        {t("contact.form.name")}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/30 focus:outline-none text-body"
                        required
                        placeholder={t("contact.form.placeholder.name")}
                      />
                    </div>
                    <div>
                      <label className="block text-body font-medium text-gray-700 mb-1">
                        {t("contact.form.email")}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/30 focus:outline-none text-body"
                        required
                        placeholder={t("contact.form.placeholder.email")}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-body font-medium text-gray-700 mb-1">
                      {t("contact.form.subject")}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/30 focus:outline-none text-body"
                      required
                      placeholder={t("contact.form.placeholder.subject")}
                    />
                  </div>

                  <div>
                    <label className="block text-body font-medium text-gray-700 mb-1">
                      {t("contact.form.message")}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/30 focus:outline-none resize-none text-body"
                      required
                      placeholder={t("contact.form.placeholder.message")}
                    ></textarea>
                  </div>

                  <ReCaptcha
                    ref={recaptchaRef}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                  />

                  <button
                    type="submit"
                    disabled={loading || !recaptchaToken}
                    className="w-full bg-primary text-white py-2 px-6 rounded-lg hover:bg-primary/90 transition-colors duration-300 flex items-center justify-center space-x-2 text-body disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <InlineSpinner size="sm" className="border-white/30 border-t-white" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        <span>{t("contact.form.send_button")}</span>
                      </>
                    )}
                  </button>
                </form>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <h2 className="text-h-5 font-semibold mb-6 text-heading">
                  {t("contact.social.title")}
                </h2>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center transition-colors duration-300 ${social.color}`}
                    >
                      <FontAwesomeIcon
                        icon={social.icon}
                        className="text-body w-5 h-5"
                      />
                      <span className="sr-only">{social.title}</span>
                    </a>
                  ))}
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="p-6 border-b">
                <h2 className="text-h-5 font-semibold flex items-center gap-2 text-heading">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-primary"
                  />
                  {t("contact.map.title")}
                </h2>
                <h3 className="text-body font-medium mt-2"> {t("contact.location.address")}</h3>
              </div>
              <div className="h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d985.1175149519256!2d38.7754249!3d8.992472!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b845420b57cc3%3A0xa1bf964f2ce6972f!2sWollo%20Sefer%2C%20Addis%20Ababa!5e0!3m2!1sen!2sus!4v1677654321890!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;